import React from 'react';
import { ID_INVITATION_PHONE_OTP_SUBMIT_BUTTON, ID_VERIFY_OTP_BACK_BUTTON } from '../../../autoIds';
import { autoId } from '@workday/upc-common';
import { ResendOTP } from '../../../shared/resend-otp/resend-otp';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import styles from './phone-verify.module.scss';
import { editIcon } from '@workday/canvas-system-icons-web';
import colors from '@workday/canvas-colors-web';
import { useEnrollmentPhoneInitMutation, useEnrollmentPhoneSubmitMutation } from '../../../slices/auth-api.slice';
import { useSelector } from 'react-redux';
import { OTPFormField, PasscodeFormState } from '../../../shared/otp-form-field/otp-form-field';
import { useForm } from 'react-hook-form';
import { selectPhoneContact } from '../../../slices/registration.slice';
import { useTranslation } from 'react-i18next';
import { useErrorHandling } from '../../../hooks/useErrorHandling';
import { serverError } from '../../../utils/server-error';

export function PhoneVerify({ steps }) {
    const [enrollmentPhoneSubmit, enrollmentPhoneSubmitMutation] = useEnrollmentPhoneSubmitMutation();

    const [enrollmentPhoneInit, enrollmentPhoneInitMutation] = useEnrollmentPhoneInitMutation();

    const phoneContact = useSelector(selectPhoneContact);

    const fullPhoneNumber = phoneContact.countryCode + phoneContact.number;

    const { control, setError, handleSubmit } = useForm<PasscodeFormState>({
        defaultValues: {
            passcode: ''
        }
    });

    function onEditClick() {
        steps.previous();
    }

    useErrorHandling(enrollmentPhoneSubmitMutation.error, {
        inlineErrors: ['UPC-1005', 'UPC-1006', 'UPC-1017'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    function onSubmit({ passcode }) {
        enrollmentPhoneSubmit({ contactId: phoneContact.contactId, passcode });
    }

    function onResend() {
        enrollmentPhoneInit({
            countryCode: phoneContact.countryCode,
            number: phoneContact.number,
            showToast: true
        });
    }

    const { t } = useTranslation('auth');

    const isLoading = enrollmentPhoneSubmitMutation.isLoading || enrollmentPhoneInitMutation.isLoading;

    return (
        <div className={styles.phoneOtpVerify}>
            <h1 className={styles.title}>{t('PhoneVerify.title')}</h1>
            <p className={styles.body}>{t('PhoneVerify.description')}</p>
            <div className={styles.phoneNumber}>
                <span>{`(***) *** - ${fullPhoneNumber.substring(fullPhoneNumber.length - 4)}`}</span>
                <TertiaryButton
                    {...autoId(ID_VERIFY_OTP_BACK_BUTTON)}
                    className={styles.editPhoneNumber}
                    onClick={onEditClick}
                    icon={editIcon}
                    size={'medium'}
                    color={colors.licorice400}
                    aria-label={t('PhoneVerify.editPhone')}
                />
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <OTPFormField
                    autoFocus={true}
                    control={control}
                    disabled={isLoading}
                    className={styles.otpFormField}
                    legend={t('PhoneVerify.description')}
                    legendVisible={false}
                />
                <PrimaryButton
                    {...autoId(ID_INVITATION_PHONE_OTP_SUBMIT_BUTTON)}
                    className={styles.button}
                    disabled={isLoading}
                    grow={true}
                    size='large'
                    type='submit'>
                    {t('PhoneVerify.submitButton')}
                </PrimaryButton>
            </form>
            <ResendOTP isDisabled={isLoading} onResend={onResend} otpType='phone' />
        </div>
    );
}
